// ========== frontend/src/GeofenceExceedances.jsx ==========
import React, { useEffect, useState } from 'react';
import axios from 'axios';

// Componente Modal para comentarios
const CommentModal = ({ exceedanceId, isOpen, onClose, user }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && exceedanceId) {
      fetchComments();
    }
  }, [isOpen, exceedanceId]);

  const fetchComments = async () => {
    try {
      const res = await axios.get(`https://apitimeinzone.ebtapps.com/api/geofence-comments/${exceedanceId}`);
      setComments(res.data);
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !user) return;

    setLoading(true);
    try {
      await axios.post('https://apitimeinzone.ebtapps.com/api/geofence-comments', {
        exceedance_id: exceedanceId,
        user_id: user.id,
        username: user.name,
        comment: newComment
      });

      setNewComment('');
      fetchComments(); // Recargar comentarios
    } catch (err) {
      console.error('Error adding comment:', err);
      alert('Error al agregar comentario');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h3>Comentarios del Registro #{exceedanceId}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          {/* Lista de comentarios existentes */}
          <div className="comments-section">
            <h4>Comentarios existentes:</h4>
            {comments.length === 0 ? (
              <p className="no-comments">No hay comentarios aún</p>
            ) : (
              <div className="comments-list">
                {comments.map(comment => (
                  <div key={comment.id} className="comment-item">
                    <div className="comment-header">
                      <strong>{comment.username}</strong>
                      <span className="comment-date">
                        {new Date(comment.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="comment-text">{comment.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Formulario para nuevo comentario */}
          <div className="add-comment-section">
            <h4>Agregar comentario:</h4>
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Escribe tu comentario aquí..."
              rows="4"
              className="comment-textarea"
              disabled={loading}
            />
            <div className="comment-actions">
              <button 
                onClick={handleAddComment}
                disabled={!newComment.trim() || loading}
                className="btn-primary"
              >
                {loading ? 'Agregando...' : 'Agregar Comentario'}
              </button>
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
};

export default function GeofenceExceedances({ user }) {
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({ 
    vehicle_name: '', 
    geofence_name: '', 
    seconds_inside: '', 
    max_seconds_inside: '',
    entered_at: '',
    exited_at: '',
    active: '' 
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedExceedanceId, setSelectedExceedanceId] = useState(null);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: rowsPerPage,
        sortBy,
        sortOrder,
        ...filters
      });
      
      const res = await axios.get(`https://apitimeinzone.ebtapps.com/api/geofence-exceedances?${params}`);
      setData(res.data.data);
      setTotalPages(res.data.totalPages);
      setTotalRecords(res.data.total);
    } catch (err) {
      console.error("Error fetching exceedances", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, rowsPerPage, filters, sortBy, sortOrder]);

  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0h 0m 0s';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${h}h ${m}m ${s}s`;
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const renderSortArrow = (column) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? '▲' : '▼';
  };

  // Función para abrir el modal de comentarios
  const openCommentModal = (exceedanceId) => {
    setSelectedExceedanceId(exceedanceId);
    setIsCommentModalOpen(true);
  };

  const columnLabels = {
    vehicle_name: 'Vehículo',
    geofence_name: 'Geocerca',
    seconds_inside: 'Tiempo Actual',
    max_seconds_inside: 'Tiempo Máximo',
    avg_time: 'Promedio Geocerca',
    entered_at: 'Hora de Entrada',
    exited_at: 'Hora de Salida',
    status: 'Estado',
    actions: 'Acciones'
  };

  return (
    <div className="dashboard-minimal">
      <div className="dashboard-header">
        <h2 className="dashboard-title">Historial de Excedencias en Geocercas</h2>
        
        <div className="filter-controls">
          <select
            value={filters.active}
            onChange={(e) => handleFilterChange('active', e.target.value)}
            className="filter-input"
          >
            <option value="">Todos los estados</option>
            <option value="true">Activos</option>
            <option value="false">Históricos</option>
          </select>
        </div>
      </div>

      <div className="table-container">
        <table className="minimal-table">
          <thead>
            <tr>
              {['vehicle_name', 'geofence_name', 'seconds_inside', 'max_seconds_inside', 'avg_time', 'entered_at', 'exited_at', 'status', 'actions'].map(col => (
                <th key={col} onClick={() => col !== 'status' && col !== 'actions' && handleSort(col)}>
                  <div className="column-header">
                    <span className="column-title">
                      {columnLabels[col]}
                      {col !== 'status' && col !== 'actions' && renderSortArrow(col)}
                    </span>
                    {col !== 'status' && col !== 'actions' && col !== 'avg_time' && (
                      <input
                        type="text"
                        value={filters[col] || ''}
                        onClick={e => e.stopPropagation()}
                        onChange={e => handleFilterChange(col, e.target.value)}
                        className="filter-input"
                        placeholder={`Filtrar ${columnLabels[col]}`}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="loading">Cargando...</td>
              </tr>
            ) : data.length > 0 ? (
              data.map((row, i) => (
                <tr key={i} className={row.exited_at ? '' : 'active-exceedance'}>
                  <td>{row.vehicle_name}</td>
                  <td>{row.geofence_name}</td>
                  <td>{formatTime(row.seconds_inside)}</td>
                  <td>{formatTime(row.max_seconds_inside)}</td>
                  <td>
                    <span className="avg-time">
                      {formatTime(row.avg_geofence_time)}
                      {row.max_seconds_inside > row.avg_geofence_time && 
                        <span className="above-avg"> ▲</span>
                      }
                      {row.max_seconds_inside < row.avg_geofence_time && 
                        <span className="below-avg"> ▼</span>
                      }
                    </span>
                  </td>
                  <td>{new Date(row.entered_at).toLocaleString()}</td>
                  <td>{row.exited_at ? new Date(row.exited_at).toLocaleString() : '-'}</td>
                  <td>
                    <span className={`status-badge ${row.exited_at ? 'completed' : 'active'}`}>
                      {row.exited_at ? 'Completado' : 'Activo'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-secondary btn-sm"
                        onClick={() => openCommentModal(row.id)}
                        title="Ver comentarios"
                        disabled={!user}
                      >
                        💬 Comentarios
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="no-data">No hay registros de excedencias</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <div className="pagination-info">
          Mostrando {data.length} de {totalRecords} registros
        </div>
        
        <div className="pagination-controls">
          <div className="rows-per-page">
            <label>Filas por página:</label>
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
            >
              {[10, 20, 50, 100].map(num => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>
          
          <div className="page-navigation">
            <button 
              className="pagination-btn"
              disabled={currentPage === 1} 
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              ‹
            </button>
            
            <span className="page-info">
              Página {currentPage} de {totalPages || 1}
            </span>
            
            <button 
              className="pagination-btn"
              disabled={currentPage === totalPages || totalPages === 0} 
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              ›
            </button>
          </div>
        </div>
      </div>

      <CommentModal 
        exceedanceId={selectedExceedanceId}
        isOpen={isCommentModalOpen}
        onClose={() => setIsCommentModalOpen(false)}
        user={user}
      />
    </div>
  );
}